import React, { useState } from 'react';
import { Card, Badge, Button, Collapse, Alert } from 'react-bootstrap';

const LocationDetailsComponent = ({ locationDetails, criteriaName }) => {
    const [showDetails, setShowDetails] = useState(false);

    if (!locationDetails) {
        return null;
    }

    const getStatusBadge = (type) => {
        switch (type) {
            case 'zone-titre-h1':
            case 'html-title':
            case 'h1-element':
            case 'meta-description':
            case 'all-headings-valid':
                return <Badge bg="success">Trouvé</Badge>;
            case 'h1-not-found':
            case 'meta-description-not-found':
            case 'empty-headings-found':
            case 'not-found':
                return <Badge bg="danger">Non trouvé</Badge>;
            case 'error':
                return <Badge bg="warning">Erreur</Badge>;
            case 'legacy':
                return <Badge bg="secondary">En cours</Badge>;
            default:
                return <Badge bg="info">Info</Badge>;
        }
    };

    const getLengthStatusBadge = (status) => {
        switch (status) {
            case 'optimal':
                return <Badge bg="success">Optimal</Badge>;
            case 'trop court':
                return <Badge bg="warning">Trop court</Badge>;
            case 'trop long':
                return <Badge bg="warning">Trop long</Badge>;
            default:
                return null;
        }
    };

    return (
        <div className="mt-3">
            <Button
                variant="outline-info"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                className="mb-2"
            >
                <i className="bi bi-geo-alt"></i> Détails de localisation
                {showDetails ? <i className="bi bi-chevron-up ms-1"></i> : <i className="bi bi-chevron-down ms-1"></i>}
            </Button>

            <Collapse in={showDetails}>
                <div>
                    <Card className="border-info">
                        <Card.Header className="bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                    <i className="bi bi-geo-alt text-info"></i> Localisation du problème
                                </h6>
                                {getStatusBadge(locationDetails.type)}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="row">
                                <div className="col-md-6">
                                    <h6><i className="bi bi-crosshair"></i> Sélecteur CSS</h6>
                                    <code className="bg-light p-2 rounded d-block">
                                        {locationDetails.selector}
                                    </code>
                                </div>
                                <div className="col-md-6">
                                    <h6><i className="bi bi-geo"></i> Emplacement</h6>
                                    <p className="mb-0">{locationDetails.location}</p>
                                </div>
                            </div>

                            {locationDetails.foundText && (
                                <div className="mt-3">
                                    <h6><i className="bi bi-file-text"></i> Texte trouvé</h6>
                                    <Alert variant="light" className="mb-0">
                                        <code>{locationDetails.foundText}</code>
                                    </Alert>
                                </div>
                            )}

                            {locationDetails.context && (
                                <div className="mt-3">
                                    <h6><i className="bi bi-info-circle"></i> Contexte</h6>
                                    <p className="text-muted mb-0">{locationDetails.context}</p>
                                </div>
                            )}

                            {locationDetails.length > 0 && (
                                <div className="mt-3">
                                    <h6><i className="bi bi-rulers"></i> Longueur</h6>
                                    <div className="d-flex align-items-center gap-2">
                                        <span>{locationDetails.length} caractères</span>
                                        {getLengthStatusBadge(locationDetails.lengthStatus)}
                                    </div>
                                </div>
                            )}

                            {/* Sections vides entre titres (spécifique text_between_titles) */}
                            {Array.isArray(locationDetails.emptySections) && locationDetails.emptySections.length > 0 && (
                                <div className="mt-3">
                                    <h6><i className="bi bi-exclamation-octagon text-danger"></i> Paires de titres sans texte / texte de qualité faible</h6>
                                    <ul className="mb-0">
                                        {locationDetails.emptySections.map((sec, idx) => (
                                            <li key={idx}>
                                                <div>
                                                    <strong>De</strong> <code>{sec.fromTitle}</code> <small>({sec.fromSelector})</small>
                                                    <strong className="ms-2">à</strong> <code>{sec.toTitle}</code> <small>({sec.toSelector})</small>
                                                    <span className="ms-2 text-muted">[position {sec.position}]</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {locationDetails.emptyHeadings && locationDetails.emptyHeadings.length > 0 && (
                                <div className="mt-3">
                                    <h6><i className="bi bi-exclamation-triangle text-warning"></i> Titres vides détectés</h6>
                                    <ul className="mb-0">
                                        {locationDetails.emptyHeadings.map((heading, index) => (
                                            <li key={index}>
                                                <code>{heading.selector}</code> - {heading.position}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="mt-3">
                                <h6><i className="bi bi-lightbulb text-warning"></i> Suggestion</h6>
                                <Alert variant="info" className="mb-0">
                                    {locationDetails.suggestion}
                                </Alert>
                            </div>

                            {/* Note sur les limitations de l'iframe */}
                            <div className="mt-3">
                                <Alert variant="secondary" className="mb-0">
                                    <small>
                                        <i className="bi bi-info-circle"></i>
                                        <strong>Note :</strong> Pour des raisons de sécurité, nous ne pouvons pas
                                        surligner directement les éléments dans l'aperçu de la page.
                                    </small>
                                </Alert>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </Collapse>
        </div>
    );
};

export default LocationDetailsComponent;